import { Tag, TagCloseButton, TagLabel, TagLeftIcon, Wrap, WrapItem } from '@chakra-ui/react'
import { Library, ListFilter } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useCommands } from '../hooks/commands/useCommands'
import { useVisibleKnowledgeSites } from '../hooks/knowledge/useVisibleKnowledgeSites'
import { describeCommand, describeCommandArgs } from '../core/parsers/commandSelection'
import { ParsedCommand } from '../types/commandTypes'

interface ScopeBadgeProps {
  icon?: React.ElementType
  label: string
  size?: 'sm' | 'md'
  onRemove?: () => void
}

export const ScopeBadge: React.FC<ScopeBadgeProps> = ({ icon, label, size = 'sm', onRemove }) => (
  <Tag size={size} variant='subtle' colorScheme='gray' borderRadius='full'>
    {icon && <TagLeftIcon as={icon} />}
    <TagLabel>{label}</TagLabel>
    {onRemove && <TagCloseButton onClick={onRemove} />}
  </Tag>
)

interface AppliedCommandTagsProps {
  commands?: ParsedCommand[]
}

/** Read-only chips for commands not handled by AnswerScopeTags. Renders nothing when empty. */
export const AppliedCommandTags: React.FC<AppliedCommandTagsProps> = ({ commands }) => {
  const { commands: descriptors } = useCommands()

  const rest = (commands ?? []).filter(
    command => command.name !== 'knowledge' && command.name !== 'org',
  )

  if (rest.length === 0) return null

  return (
    <Wrap spacing={2} mt={1}>
      {rest.map(command => (
        <WrapItem key={command.name}>
          <Tag size='sm' variant='subtle' colorScheme='brand' borderRadius='full'>
            <TagLabel>{describeCommand(command, descriptors)}</TagLabel>
          </Tag>
        </WrapItem>
      ))}
    </Wrap>
  )
}

interface AnswerScopeTagsProps {
  commands?: ParsedCommand[]
}

/**
 * Flag-style chips echoing the menu directives a reply ran under — per-site
 * knowledge toggles and the `org` filter ("Filtered to: KBC, SLOB"). Both
 * share one gray, subtle, icon style and sit next to the sources bubble in
 * the answer's action row. Renders nothing on default turns.
 */
export const AnswerScopeTags: React.FC<AnswerScopeTagsProps> = ({ commands }) => {
  const { t } = useTranslation()
  const { commands: descriptors } = useCommands()
  const { sites: toggleableKnowledgeSites } = useVisibleKnowledgeSites()

  const list = commands ?? []
  const disabledSourceKeys = new Set(
    list
      .filter(cmd => cmd.name === 'knowledge' && cmd.args.length === 2 && cmd.args[1] === 'off')
      .map(cmd => cmd.args[0]),
  )
  const disabledSites = toggleableKnowledgeSites.filter(site => disabledSourceKeys.has(site.key))

  const filter = list.find(command => command.name === 'org' && command.args.length > 0)

  if (disabledSites.length === 0 && !filter) return null

  return (
    <>
      {disabledSites.map(site => (
        <ScopeBadge
          key={site.key}
          icon={Library}
          label={t('chat.knowledgeSourceOff', { name: site.name })}
        />
      ))}
      {filter && (
        <ScopeBadge
          icon={ListFilter}
          label={`${t('chat.filteredTo')}: ${describeCommandArgs(filter, descriptors)}`}
        />
      )}
    </>
  )
}
